import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

/* OB Tracker (user, 22 Oct 2026) — manual order-block paper journal.
   XAUUSDT only. User spots OBs on 1m/5m/15m by eye; entry = OB centre,
   TP = opposing OB centre, SL their call. Module records + auto-resolves
   on 1m candles (SL-first on both-touch) and facets the results so a
   strategy can be mined out of 100-200 trades. Paper only. */

const rup = (v) => v == null ? "—" : `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
const usd = (v) => v == null ? "—" : `$${(+v).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const when = (s) => { try { return new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };
const TFS = ["1m", "5m", "15m"];
// backtest times are 15m BAR OPENS — the fill happened somewhere inside the
// bar, so show the window, not a minute (validation of trade #229, 23 Oct)
const barWin = (s) => {
  if (!s) return "—";
  try {
    const a = new Date(s), b = new Date(a.getTime() + 15 * 60000);
    const o = { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false };
    const day = a.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" });
    return `${day}, ${a.toLocaleTimeString("en-IN", o)}–${b.toLocaleTimeString("en-IN", o)}`;
  } catch { return s; }
};

/* Inline candle chart for one backtest trade: entry/SL/TP1/exit drawn on the
   exact 15m bars the engine saw. Pure SVG, no deps. */
function TradeChart({ candles, t }) {
  const e0 = Date.parse(t.entry_time), x0 = Date.parse(t.exit_time);
  const i0 = candles.findIndex(c => c.t >= e0);
  if (i0 < 0) return <div style={{ fontSize: 11, color: "var(--muted2)" }}>candles unavailable for this window</div>;
  let i1 = candles.findIndex(c => c.t >= x0);
  if (i1 < 0) i1 = candles.length - 1;
  const a = Math.max(0, i0 - 12), b = Math.min(candles.length, Math.min(i1 + 13, a + 140));
  const win = candles.slice(a, b);
  const levels = [t.entry, t.sl, t.tp1, t.exit].filter(v => v != null);
  const lo = Math.min(...win.map(c => c.l), ...levels), hi = Math.max(...win.map(c => c.h), ...levels);
  const pad = (hi - lo) * 0.08 || 1;
  const W = 760, H = 260, L = 6, R = 64, ymin = lo - pad, ymax = hi + pad;
  const plotW = W - L - R, n = win.length, cw = plotW / n;
  const X = (i) => L + i * cw + cw / 2;
  const Y = (p) => 4 + (H - 24) * (1 - (p - ymin) / (ymax - ymin));
  const line = (px, color, label) => (
    <g key={label}>
      <line x1={L} y1={Y(px)} x2={W - R + 2} y2={Y(px)} stroke={color} strokeDasharray="4 3" strokeWidth="1" />
      <text x={W - R + 5} y={Y(px) + 3.5} fontSize="10" fill={color}>{label} {px}</text>
    </g>);
  const ei = i0 - a, xi2 = Math.min(i1, b - 1) - a;
  const tpi = t.tp1_time ? Math.max(0, candles.findIndex(c => c.t >= Date.parse(t.tp1_time)) - a) : null;
  const fmt = (ms) => new Date(ms).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <svg width={W} height={H} style={{ maxWidth: "100%", background: "var(--chart-bg)", borderRadius: 6 }}>
      {win.map((c, i) => {
        const up = c.c >= c.o;
        const col = up ? "var(--pos, #22c55e)" : "var(--neg, #ef4444)";
        const bw = Math.max(2, cw * 0.6);
        return (<g key={c.t}>
          <line x1={X(i)} y1={Y(c.h)} x2={X(i)} y2={Y(c.l)} stroke={col} strokeWidth="1" />
          <rect x={X(i) - bw / 2} y={Y(Math.max(c.o, c.c))} width={bw}
            height={Math.max(1, Math.abs(Y(c.o) - Y(c.c)))} fill={col} />
        </g>);
      })}
      {line(t.entry, "var(--muted2, #94a3b8)", "entry")}
      {line(t.sl, "var(--neg, #ef4444)", "SL")}
      {line(t.tp1, "var(--pos, #22c55e)", "TP1")}
      {t.exit !== t.tp1 && line(t.exit, "#eab308", "exit")}
      <text x={X(ei)} y={Y(t.entry) - 7} fontSize="12" textAnchor="middle" fill="var(--text2, #e2e8f0)">▼ short</text>
      {tpi != null && t.tp1_hit && <text x={X(tpi)} y={Y(t.tp1) + 14} fontSize="12" textAnchor="middle" fill="var(--pos, #22c55e)">✓ 70%</text>}
      <circle cx={X(xi2)} cy={Y(t.exit)} r="4" fill="#eab308" stroke="var(--text2, #fff)" strokeWidth="1" />
      <text x={X(xi2)} y={Y(t.exit) + 16} fontSize="10" textAnchor="middle" fill="#eab308">{t.exit_reason}</text>
      <text x={L} y={H - 4} fontSize="10" fill="var(--muted2)">{fmt(win[0].t)} IST</text>
      <text x={W - R} y={H - 4} fontSize="10" textAnchor="end" fill="var(--muted2)">{fmt(win[win.length - 1].t)} IST · 15m XAUTUSDT</text>
    </svg>
  );
}

export default function OBTracker() {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [page, setPage] = useState(0);
  const [bt, setBt] = useState(null);          // OB-S backtest trades (lazy)
  const [btOpen, setBtOpen] = useState(false);
  const [btPage, setBtPage] = useState(0);
  const [btCandles, setBtCandles] = useState(null);  // full 15m series (lazy, once)
  const [btSel, setBtSel] = useState(null);          // entry_time of the charted trade
  const blank = { tf: "5m", side: "LONG", zone_mode: "body", qty: "", entry: "", sl: "", tp: "",
                  ob_top: "", ob_bottom: "", tp_tf: "", fresh: null, with_trend: null, note: "" };
  const [f, setF] = useState(blank);

  const load = useCallback(async () => {
    try {
      const r = await axios.get("/api/ob-tracker/trades", { timeout: 30000 });
      if (r.data?.success) { setSt(r.data); setErr(""); } else setErr(r.data?.error || "load failed");
    } catch (e) { setErr(String(e?.message || e)); }
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const chip = (active) => ({
    fontSize: 11, padding: "3px 10px", borderRadius: 12, cursor: "pointer",
    border: active ? "1px solid var(--accent, #6366f1)" : "1px solid var(--panel3)",
    background: active ? "var(--tint-pos)" : "var(--chart-bg)",
    color: active ? "var(--text2)" : "var(--muted2)", fontWeight: active ? 700 : 500,
  });
  const num = (v) => { const x = parseFloat(v); return isNaN(x) ? null : x; };

  // live previews while typing
  const fx = st?.fx || 88.5;
  const lev = st?.leverage || 75;
  const feeSide = st?.feeSide || 0.000472;
  const qty = num(f.qty), entry = num(f.entry), sl = num(f.sl), tp = num(f.tp);
  const d = f.side === "LONG" ? 1 : -1;
  const notional = qty && entry ? qty * entry : null;
  const margin = notional ? notional / lev : null;
  const riskUsd = qty && entry && sl ? Math.abs(entry - sl) * qty + (entry + sl) * qty * feeSide : null;
  const rewardUsd = qty && entry && tp ? d * (tp - entry) * qty - (entry + tp) * qty * feeSide : null;
  const rr = riskUsd && rewardUsd != null ? rewardUsd / riskUsd : null;

  const save = async () => {
    setMsg("");
    const r = await axios.post("/api/ob-tracker/trades", { ...f, qty, entry, sl, tp,
      ob_top: num(f.ob_top), ob_bottom: num(f.ob_bottom), tp_tf: f.tp_tf || null });
    if (r.data?.success) { setF({ ...blank, tf: f.tf, side: f.side, zone_mode: f.zone_mode }); setMsg("Saved — auto-tracking on 1m candles"); load(); }
    else setMsg(r.data?.error || "save failed");
    setTimeout(() => setMsg(""), 5000);
  };
  const closeTrade = async (t) => {
    const px = window.prompt(`Close ${t.side} ${t.tf} — exit price:`, st?.livePrice ?? "");
    if (px == null) return;
    await axios.post(`/api/ob-tracker/trades/${t.id}/close`, { price: parseFloat(px) || null });
    load();
  };
  const del = async (t) => {
    if (!window.confirm("Delete this entry? (for mistakes only — closed results should stay)")) return;
    await axios.delete(`/api/ob-tracker/trades/${t.id}`);
    load();
  };

  if (!st) return <div className="loading"><div className="spinner" /><p>{err || "Loading OB tracker…"}</p></div>;

  const open = st.trades.filter(t => t.status === "OPEN");
  const closed = st.trades.filter(t => t.status === "CLOSED");
  const pages = Math.max(1, Math.ceil(closed.length / 15));
  const S = st.stats || {};
  const livePnl = (t) => st.livePrice == null ? null :
    ((t.side === "LONG" ? 1 : -1) * (st.livePrice - t.entry) * t.qty - (t.entry + st.livePrice) * t.qty * feeSide) * fx;

  const statRow = (label, s) => (
    <tr key={label}><td>{label}</td><td>{s.n}</td><td>{s.wr}%</td>
      <td className={s.net_inr >= 0 ? "positive" : "negative"}>{rup(s.net_inr)}</td>
      <td className={s.net_r >= 0 ? "positive" : "negative"}>{s.net_r}R</td><td>{s.avg_r}R</td></tr>
  );
  const facetTable = (title, data) => (
    <div key={title} style={{ minWidth: 230 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", marginBottom: 4 }}>{title}</div>
      <table className="stocks-table" style={{ width: "100%" }}>
        <thead><tr><th></th><th>n</th><th>WR</th><th>Net ₹</th><th>Net R</th><th>Avg R</th></tr></thead>
        <tbody>{Object.entries(data || {}).map(([k, s]) => statRow(k, s))}</tbody>
      </table>
    </div>
  );

  return (
    <div>
      <h3 style={{ margin: "4px 0 2px" }}>🧱 OB Tracker — manual order-block paper journal</h3>
      <div style={{ fontSize: 11.5, color: "var(--muted2)", marginBottom: 10 }}>
        XAUUSDT only · entry = OB centre, TP = opposing OB centre · auto-resolved on 1m candles (both-touch counts as SL, pessimistic) ·
        fees {(feeSide * 100).toFixed(4)}%/side · leverage {lev}x · USDT/INR {fx} · live <b>{st.livePrice ?? "—"}</b> ·
        goal: 100–200 trades, then mine the facets. <b>{S.all?.n ?? 0}</b> closed so far.
      </div>

      {/* entry form */}
      <div style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 10.5, color: "var(--muted2)" }}>OB TF:</span>
          {TFS.map(tf => <button key={tf} style={chip(f.tf === tf)} onClick={() => set("tf")(tf)}>{tf}</button>)}
          <span style={{ fontSize: 10.5, color: "var(--muted2)", marginLeft: 8 }}>Side:</span>
          <button style={chip(f.side === "LONG")} onClick={() => set("side")("LONG")}>🎯 LONG (buy-side OB)</button>
          <button style={chip(f.side === "SHORT")} onClick={() => set("side")("SHORT")}>🔻 SHORT (sell-side OB)</button>
          <span style={{ fontSize: 10.5, color: "var(--muted2)", marginLeft: 8 }} title="How you drew the OB zone">Zone:</span>
          <button style={chip(f.zone_mode === "body")} onClick={() => set("zone_mode")("body")} title="OB drawn open-to-close (body only)">Body only</button>
          <button style={chip(f.zone_mode === "wick")} onClick={() => set("zone_mode")("wick")} title="OB drawn high-to-low (wick included)">Wick incl.</button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 8 }}>
          {[["qty", "Qty (oz)"], ["entry", "Entry = OB centre"], ["sl", "SL"], ["tp", "TP = opposing OB centre"],
            ["ob_top", "OB top (opt)"], ["ob_bottom", "OB bottom (opt)"]].map(([k, label]) => (
            <label key={k} style={{ fontSize: 10.5, color: "var(--muted2)" }}>{label}<br />
              <input className="ema-select" style={{ width: 110, fontSize: 12 }} value={f[k]} onChange={set(k)} placeholder={k === "entry" && st.livePrice ? String(st.livePrice) : ""} />
            </label>
          ))}
          <label style={{ fontSize: 10.5, color: "var(--muted2)" }}>TP's OB TF<br />
            <select className="ema-select" style={{ fontSize: 12 }} value={f.tp_tf} onChange={set("tp_tf")}>
              <option value="">—</option>{TFS.map(tf => <option key={tf} value={tf}>{tf}</option>)}
            </select></label>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 10.5, color: "var(--muted2)" }}>Zone state:</span>
          <button style={chip(f.fresh === true)} onClick={() => set("fresh")(f.fresh === true ? null : true)}>fresh (1st touch)</button>
          <button style={chip(f.fresh === false)} onClick={() => set("fresh")(f.fresh === false ? null : false)}>retested</button>
          <span style={{ fontSize: 10.5, color: "var(--muted2)", marginLeft: 8 }}>Trend:</span>
          <button style={chip(f.with_trend === true)} onClick={() => set("with_trend")(f.with_trend === true ? null : true)}>with trend</button>
          <button style={chip(f.with_trend === false)} onClick={() => set("with_trend")(f.with_trend === false ? null : false)}>counter</button>
          <input className="ema-select" style={{ flex: 1, minWidth: 160, fontSize: 12 }} value={f.note} onChange={set("note")} placeholder="note (optional)" />
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 12 }}>
          <span>Notional: <b>{usd(notional)}</b>{notional ? ` (₹${Math.round(notional * fx).toLocaleString("en-IN")})` : ""}</span>
          <span>Margin @{lev}x: <b>{usd(margin)}</b>{margin ? ` (₹${Math.round(margin * fx).toLocaleString("en-IN")})` : ""}</span>
          <span>Risk: <b className="negative">{riskUsd ? rup(-riskUsd * fx) : "—"}</b></span>
          <span>Reward at TP: <b className="positive">{rewardUsd != null ? rup(rewardUsd * fx) : "—"}</b></span>
          <span>R:R: <b>{rr != null ? `1 : ${rr.toFixed(2)}` : "—"}</b> <span style={{ color: "var(--muted2)", fontSize: 10.5 }}>(fee-honest)</span></span>
          <button className="btn btn-sm btn-primary" style={{ marginLeft: "auto" }} onClick={save}
            disabled={!qty || !entry || !sl || !tp}>➕ Record paper trade</button>
          {msg && <span style={{ fontSize: 11, color: "var(--muted2)" }}>{msg}</span>}
        </div>
      </div>

      {/* open trades */}
      {open.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", marginBottom: 4 }}>Open ({open.length}) — auto-tracking</div>
          <div className="stocks-table-wrapper tl-table-wrap"><table className="stocks-table" style={{ width: "100%" }}>
            <thead><tr><th>Entered</th><th>TF</th><th>Side</th><th>Zone</th><th>Qty</th><th>Entry</th><th>SL</th><th>TP</th><th>Margin</th><th>Live P&L</th><th></th></tr></thead>
            <tbody>{open.map(t => (
              <tr key={t.id}>
                <td>{when(t.entry_time)}</td><td>{t.tf}</td><td>{t.side}</td>
                <td style={{ fontSize: 10.5 }}>{t.zone_mode === "body" ? "body" : t.zone_mode === "wick" ? "wick" : "—"}</td>
                <td>{t.qty}</td><td>{t.entry}</td><td className="sl">{t.sl}</td><td className="positive">{t.tp}</td>
                <td>{rup(t.entry * t.qty / lev * fx).slice(1)}</td>
                <td className={(livePnl(t) ?? 0) >= 0 ? "positive" : "negative"}>{rup(livePnl(t))}</td>
                <td><button className="btn btn-sm btn-secondary" onClick={() => closeTrade(t)}>close</button>{" "}
                  <button className="btn btn-sm btn-secondary" onClick={() => del(t)}>✕</button></td>
              </tr>))}</tbody>
          </table></div>
        </div>
      )}

      {/* stats */}
      {S.all?.n > 0 && (
        <div style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12.5, marginBottom: 10 }}>
            <span><b>{S.all.n}</b> closed</span>
            <span>WR <b>{S.all.wr}%</b></span>
            <span>Net <b className={S.all.net_inr >= 0 ? "positive" : "negative"}>{rup(S.all.net_inr)}</b></span>
            <span>Net <b className={S.all.net_r >= 0 ? "positive" : "negative"}>{S.all.net_r}R</b></span>
            <span>Expectancy <b>{S.all.avg_r}R</b>/trade</span>
            <span>Fees paid <b>{rup(S.all.fees_inr).slice(1)}</b></span>
            <a className="btn btn-sm btn-secondary" style={{ marginLeft: "auto" }} href="/api/ob-tracker/export" download="ob_tracker.csv">⬇ CSV</a>
          </div>
          {st.equity?.length > 1 && (() => {
            const vs = st.equity.map(p => p.v); const mn = Math.min(0, ...vs), mx = Math.max(0, ...vs);
            const W = 560, H = 80, sx = W / (vs.length - 1), sy = mx > mn ? H / (mx - mn) : 1;
            const pts = vs.map((v, i) => `${(i * sx).toFixed(1)},${(H - (v - mn) * sy).toFixed(1)}`).join(" ");
            return (<svg width={W} height={H} style={{ display: "block", marginBottom: 10, maxWidth: "100%" }}>
              <line x1="0" y1={H - (0 - mn) * sy} x2={W} y2={H - (0 - mn) * sy} stroke="var(--panel3)" strokeDasharray="3 3" />
              <polyline points={pts} fill="none" stroke={vs[vs.length - 1] >= 0 ? "var(--pos, #22c55e)" : "var(--neg, #ef4444)"} strokeWidth="1.6" />
            </svg>);
          })()}
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {facetTable("By OB timeframe", S.by_tf)}
            {facetTable("By side", S.by_side)}
            {facetTable("By zone drawing", S.by_zone)}
            {facetTable("Fresh vs retested", S.by_fresh)}
            {facetTable("Trend alignment", S.by_trend)}
            {facetTable("By exit", S.by_exit)}
            {facetTable("By session (IST)", S.by_session)}
          </div>
        </div>
      )}

      {/* closed trades */}
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", marginBottom: 4 }}>Closed ({closed.length})</div>
      <div className="stocks-table-wrapper tl-table-wrap"><table className="stocks-table" style={{ width: "100%" }}>
        <thead><tr><th>Entered</th><th>Exited</th><th>TF</th><th>Side</th><th>Zone</th><th>Qty</th><th>Entry</th><th>SL</th><th>TP</th><th>Exit</th><th>Reason</th><th>Gross</th><th>Fees</th><th>Net ₹</th><th>R</th><th></th></tr></thead>
        <tbody>
          {closed.slice(page * 15, page * 15 + 15).map(t => (
            <tr key={t.id}>
              <td>{when(t.entry_time)}</td><td>{when(t.exit_time)}</td><td>{t.tf}</td><td>{t.side}</td>
              <td style={{ fontSize: 10.5 }}>{t.zone_mode === "body" ? "body" : t.zone_mode === "wick" ? "wick" : "—"}</td>
              <td>{t.qty}</td><td>{t.entry}</td><td>{t.sl}</td><td>{t.tp}</td><td>{t.exit_price}</td>
              <td style={{ fontSize: 10.5 }}>{t.exit_reason}</td>
              <td>{rup(t.gross_inr)}</td><td>{rup(-(t.fees_usd || 0) * (t.fx_exit || fx)).slice(1)}</td>
              <td className={(t.net_inr || 0) >= 0 ? "positive" : "negative"}><b>{rup(t.net_inr)}</b></td>
              <td className={(t.r_mult || 0) >= 0 ? "positive" : "negative"}>{t.r_mult}</td>
              <td><button className="btn btn-sm btn-secondary" onClick={() => del(t)}>✕</button></td>
            </tr>))}
          {!closed.length && <tr><td colSpan={16} style={{ color: "var(--muted2)" }}>No closed trades yet — record a setup above; it auto-resolves on 1m candles.</td></tr>}
        </tbody>
      </table></div>
      {pages > 1 && <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        {Array.from({ length: pages }, (_, i) => <button key={i} style={chip(page === i)} onClick={() => setPage(i)}>{i + 1}</button>)}
      </div>}

      {/* OB-S backtest trade list — chart-validation reference (Round 24e) */}
      <div style={{ marginTop: 18 }}>
        <button className="btn btn-sm btn-secondary" onClick={async () => {
          if (!bt) { try { const r = await axios.get("/api/ob-tracker/backtest", { timeout: 30000 }); if (r.data?.success) setBt(r.data); } catch {} }
          setBtOpen(!btOpen);
        }}>{btOpen ? "▾" : "▸"} OB-S backtest trades (Round 24e) — validate on chart</button>
        {btOpen && bt && (() => {
          const m = bt.meta || {}; const rows = bt.trades || [];
          const bp = Math.max(1, Math.ceil(rows.length / 15));
          return (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11.5, color: "var(--muted2)", marginBottom: 6 }}>
                {m.study}<br />
                <b>{m.n}</b> trades · WR <b>{m.wr}%</b> · net <b className={m.net_inr >= 0 ? "positive" : "negative"}>{rup(m.net_inr)}</b> ({m.net_r}R) at {m.qty_oz} oz, fx {m.fx} · {m.span}<br />
                {m.fees} · funding: {m.funding}<br />
                ⚠ {m.note}<br />Times are 15m bar windows (IST): the fill happened somewhere inside that bar — check 1m candles for the exact minute.
              </div>
              <div className="stocks-table-wrapper tl-table-wrap"><table className="stocks-table" style={{ width: "100%" }}>
                <thead><tr><th>#</th><th title="15m bar in which the entry filled (IST)">Entry bar (IST)</th><th>Entry</th><th>SL</th><th>TP1 (+100p)</th><th title="15m bar in which TP1 filled (IST)">TP1 bar (IST)</th><th>Final exit</th><th title="15m bar in which the final exit filled (IST)">Exit bar (IST)</th><th>Why</th><th>Pips tp1/runner</th><th>Comm</th><th>Funding</th><th>Net ₹</th><th>R</th></tr></thead>
                <tbody>
                  {rows.slice(btPage * 15, btPage * 15 + 15).map((t, i) => (
                    <React.Fragment key={t.entry_time}>
                      <tr onClick={async () => {
                        if (!btCandles) { try { const r = await axios.get("/api/ob-tracker/backtest/candles", { timeout: 60000 }); if (r.data?.success) setBtCandles(r.data.candles); } catch {} }
                        setBtSel(btSel === t.entry_time ? null : t.entry_time);
                      }} style={{ cursor: "pointer" }} title="Click to show the chart (entry / SL / TP1 / exit)">
                        <td>{btSel === t.entry_time ? "▾" : "▸"} {btPage * 15 + i + 1}</td>
                        <td>{barWin(t.entry_time)}</td><td>{t.entry}</td><td className="sl">{t.sl}</td>
                        <td>{t.tp1}{t.tp1_hit ? " ✓" : " ✗"}</td>
                        <td>{t.tp1_time ? barWin(t.tp1_time) : "—"}</td>
                        <td>{t.exit}</td><td>{barWin(t.exit_time)}</td>
                        <td style={{ fontSize: 10.5 }}>{t.exit_reason}</td>
                        <td>{t.pips_tp1 ?? "—"} / {t.pips_runner}</td>
                        <td>{Math.round(t.commission_inr)}</td><td>{Math.round(t.funding_inr)}</td>
                        <td className={t.net_inr >= 0 ? "positive" : "negative"}><b>{rup(t.net_inr)}</b></td>
                        <td className={t.r_mult >= 0 ? "positive" : "negative"}>{t.r_mult}</td>
                      </tr>
                      {btSel === t.entry_time && (
                        <tr><td colSpan={14} style={{ padding: "6px 4px" }}>
                          {btCandles ? <TradeChart candles={btCandles} t={t} />
                            : <span style={{ fontSize: 11, color: "var(--muted2)" }}>loading candles…</span>}
                        </td></tr>
                      )}
                    </React.Fragment>))}
                </tbody>
              </table></div>
              {bp > 1 && <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                {Array.from({ length: bp }, (_, i) => <button key={i} style={chip(btPage === i)} onClick={() => setBtPage(i)}>{i + 1}</button>)}
              </div>}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
